import React, { useEffect, useState } from 'react';
import {
  Settings as SettingsIcon,
  CheckCircle2,
  Loader2,
  Save,
  Plus,
  Trash2,
  Eye,
} from 'lucide-react';
import api from '@/lib/axios';

/**
 * Configurações editáveis do Marketplace (admin).
 * Permite controlar campanha gratuita, preço único, duração, texto comercial e packs.
 */
export default function MarketplaceSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/marketplace/settings');
      setSettings(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar configurações.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      const payload = {
        ...settings,
        preco_unico: Number(settings.preco_unico) || 0,
        duracao_dias: Number(settings.duracao_dias) || 30,
        campanha_gratuita_dias: Number(settings.campanha_gratuita_dias) || 30,
        packs: (settings.packs || []).map((p) => ({
          label: p.label || '',
          price: Number(p.price) || 0,
          quantity: Number(p.quantity) || 1,
          days: Number(p.days) || 30,
        })),
      };
      const res = await api.put('/admin/marketplace/settings', payload);
      setSettings(res.data);
      setSuccessMsg('Configurações guardadas. Mudanças já visíveis no site público.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar configurações.');
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => setSettings((s) => ({ ...s, [field]: value }));

  const updatePack = (idx, field, value) =>
    setSettings((s) => ({
      ...s,
      packs: s.packs.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    }));

  const addPack = () =>
    setSettings((s) => ({
      ...s,
      packs: [...(s.packs || []), { label: '', price: 0, quantity: 5, days: 30 }],
    }));

  const removePack = (idx) =>
    setSettings((s) => ({ ...s, packs: s.packs.filter((_, i) => i !== idx) }));

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 p-6" data-testid="marketplace-settings-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> A carregar configurações...
      </div>
    );
  }

  if (!settings) return null;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 space-y-6"
      data-testid="marketplace-settings-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <SettingsIcon className="h-6 w-6 text-emerald-700" />
          <h2 className="text-xl font-bold text-slate-900">Configurações do Marketplace</h2>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="marketplace-settings-save-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:bg-slate-400"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'A guardar...' : 'Guardar alterações'}
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" /> {successMsg}
        </div>
      )}

      {/* Switch Campanha Gratuita */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!settings.campanha_gratuita_ativa}
            onChange={(e) => update('campanha_gratuita_ativa', e.target.checked)}
            className="mt-1 h-5 w-5 accent-emerald-600"
            data-testid="marketplace-settings-free-toggle"
          />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Campanha Gratuita Ativa</p>
            <p className="text-xs text-amber-800 mt-1">
              Quando ligado, todos os novos anúncios são <strong>gratuitos</strong> e ficam
              directamente pendentes para aprovação. Quando desligado, o sistema redirecciona
              cada novo anúncio para <strong>checkout Stripe</strong> (Cartão / MBWay / Multibanco)
              antes de ficar disponível para aprovação.
            </p>
          </div>
        </label>
        {settings.campanha_gratuita_ativa && (
          <div className="mt-3 ml-8 flex items-center gap-2 text-sm text-amber-800">
            Duração dos anúncios gratuitos:
            <input
              type="number"
              min={1}
              max={365}
              value={settings.campanha_gratuita_dias}
              onChange={(e) => update('campanha_gratuita_dias', e.target.value)}
              className="w-20 rounded border border-amber-300 px-2 py-1"
              data-testid="marketplace-settings-free-days"
            />
            dias
          </div>
        )}
      </div>

      {/* Preço e duração */}
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Preço por anúncio (€)</label>
          <input
            type="number"
            step="0.50"
            min={0}
            value={settings.preco_unico}
            onChange={(e) => update('preco_unico', e.target.value)}
            data-testid="marketplace-settings-price"
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Duração (dias)</label>
          <input
            type="number"
            min={1}
            max={365}
            value={settings.duracao_dias}
            onChange={(e) => update('duracao_dias', e.target.value)}
            data-testid="marketplace-settings-duration"
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Moeda</label>
          <select
            value={settings.moeda}
            onChange={(e) => update('moeda', e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
            data-testid="marketplace-settings-currency"
          >
            <option value="EUR">EUR (€)</option>
          </select>
        </div>
      </div>

      {/* Texto comercial */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Texto comercial (aparece no Marketplace público)
        </label>
        <textarea
          rows={3}
          value={settings.texto_comercial}
          onChange={(e) => update('texto_comercial', e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2"
          placeholder="Ex: Anuncie a sua viatura por apenas €7 / 30 dias. Pagamento por MBWay, Cartão ou Multibanco."
          data-testid="marketplace-settings-text"
        />
      </div>

      {/* Banner promocional */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Banner promocional (texto curto destacado no topo)
        </label>
        <input
          type="text"
          maxLength={140}
          value={settings.banner_promocional || ''}
          onChange={(e) => update('banner_promocional', e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2"
          placeholder="Ex: Promoção de lançamento — primeiros 100 anúncios grátis"
          data-testid="marketplace-settings-banner"
        />
        <p className="text-xs text-slate-500 mt-1">Deixe vazio para não mostrar banner.</p>
      </div>

      {/* Preview */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <p className="flex items-center gap-1 text-xs font-semibold text-emerald-700 mb-2">
          <Eye className="h-3 w-3" /> Pré-visualização no Marketplace público
        </p>
        {settings.banner_promocional && (
          <div className="rounded-lg bg-emerald-600 text-white text-sm font-semibold px-3 py-2 mb-2">
            🎉 {settings.banner_promocional}
          </div>
        )}
        <div className="rounded-lg bg-white border border-emerald-300 p-3 text-sm text-slate-700 whitespace-pre-wrap">
          {settings.texto_comercial || <em className="text-slate-400">Sem texto comercial.</em>}
        </div>
        <div className="mt-2 text-xs text-emerald-800">
          {settings.campanha_gratuita_ativa ? (
            <span>
              ✅ Anúncios <strong>gratuitos</strong> ({settings.campanha_gratuita_dias} dias)
            </span>
          ) : (
            <span>
              💳 <strong>{Number(settings.preco_unico).toFixed(2)} {settings.moeda}</strong> por anúncio
              ({settings.duracao_dias} dias)
            </span>
          )}
        </div>
      </div>

      {/* Packs */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-700">Packs (opcional)</h3>
          <button
            onClick={addPack}
            data-testid="marketplace-settings-add-pack"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100"
          >
            <Plus className="h-3 w-3" /> Adicionar pack
          </button>
        </div>
        {settings.packs.length === 0 ? (
          <p className="text-xs text-slate-500 italic">Sem packs definidos. Apenas o preço unitário aplica.</p>
        ) : (
          <div className="space-y-2">
            {settings.packs.map((p, idx) => (
              <div
                key={idx}
                className="grid grid-cols-12 gap-2 items-center rounded-lg border border-slate-200 p-2"
                data-testid={`marketplace-settings-pack-${idx}`}
              >
                <input
                  type="text"
                  placeholder="Nome do pack"
                  value={p.label}
                  onChange={(e) => updatePack(idx, 'label', e.target.value)}
                  className="col-span-4 rounded border border-slate-300 px-2 py-1 text-sm"
                />
                <div className="col-span-2">
                  <input
                    type="number"
                    placeholder="Quantos"
                    min={1}
                    value={p.quantity}
                    onChange={(e) => updatePack(idx, 'quantity', e.target.value)}
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    placeholder="Dias"
                    min={1}
                    value={p.days}
                    onChange={(e) => updatePack(idx, 'days', e.target.value)}
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                  />
                </div>
                <div className="col-span-3">
                  <input
                    type="number"
                    placeholder="Preço (€)"
                    step="0.50"
                    min={0}
                    value={p.price}
                    onChange={(e) => updatePack(idx, 'price', e.target.value)}
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                  />
                </div>
                <button
                  onClick={() => removePack(idx)}
                  className="col-span-1 flex justify-center text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
